import { Injectable, ResourceRef, Signal, inject, computed } from "@angular/core";
import { HttpParams } from "@angular/common/http";
import { Observable, catchError, map, of, tap } from "rxjs";
import { environment } from "src/environments/environment";
import { UnitAttributes, UnitNavigationInfoDto } from "src/models/units/unit-attributes.model";
import { HttpDataService, IResponseWrapper } from "./httpdata.service";
import { AddressDto } from "src/models/units/address.model";
import { INeighborhoodInfoModel } from "src/models/units/neighborhood.model";
import { IUnitShareInfo } from "src/app/infopage/models/unit-share-info.model";
import { OrganizationUnitKeyInfo } from "src/app/infopage/models/organization-unit-keyinfo.model";

@Injectable({
    providedIn: "root",
})
export class UnitDataService {
    private readonly http = inject(HttpDataService);
    private unitNavigationInfoCache = new Map<number, UnitNavigationInfoDto>();

    getUnitAttributes(params: string): Observable<UnitAttributes[]> {
        var searchParams = {
            pattern: params,
            maxCount: 1,
        };
        try {
            return this.http
                .get<IResponseWrapper<UnitAttributes[]>>(`${environment.apiUrl}/Units/UnitAttributes`, searchParams)
                .pipe(map((response) => response.data));
        } catch (ex) {
            console.log("Error: ", ex);
            return of(null);
        }
    }

    getUnitInfoBasic(unitID: string): Observable<UnitAttributes> {
        try {
            return this.http
                .get<IResponseWrapper<UnitAttributes>>(`${environment.apiUrl}/Units/UnitInfoBasic?unitId=${unitID}`)
                .pipe(map((response) => response.data));
        } catch (ex) {
            console.log("Error: ", ex);
            return of(null);
        }
    }
    getUnitNavigationInfo(unitId: string): Observable<UnitNavigationInfoDto> {
        try {
            return this.http
                .get<IResponseWrapper<UnitNavigationInfoDto>>(`${environment.apiUrl}/Units/${unitId}/UnitNavigationInfo`)
                .pipe(map((response) => response.data));
        } catch (ex) {
            console.log("Error: ", ex);
            return of(null);
        }
    }

    getPublicAddressInfo(unitId: number) {
        return this.http.get<IResponseWrapper<AddressDto[]>>(`${environment.apiUrl}/Units/${unitId}/PublicAddressInfo`).pipe(map((response) => response.data));
    }

    getNeighbourhoodData(unitId: number): Observable<INeighborhoodInfoModel> {
        return this.http.get<{ data: INeighborhoodInfoModel }>(`${environment.apiUrl}/Units/${unitId}/Neighborhood`).pipe(map((response) => response.data));
    }

    getUnitAttributesResource(params: Signal<string>): ResourceRef<UnitAttributes[] | null> {
        const url = computed(() => {
            const searchPattern = params();
            if (searchPattern) {
                const httpParams = new HttpParams().set("pattern", searchPattern).set("maxCount", "1");
                return `${environment.apiUrl}/Units/UnitAttributes?${httpParams.toString()}`;
            }
            return null;
        });

        return this.http.getResource<UnitAttributes[]>(url);
    }

    getUnitInfoBasicResource(unitId: Signal<string>): ResourceRef<UnitAttributes | null> {
        const url = computed(() => (unitId() ? `${environment.apiUrl}/Units/UnitInfoBasic?unitId=${unitId()}` : null));
        return this.http.getResource<UnitAttributes>(url);
    }


    getUnitNavigationInfoObservable(unitId: number): Observable<UnitNavigationInfoDto> {
        if (!unitId) {
            return of(null);
        }
        if (unitId && this.unitNavigationInfoCache.has(unitId)) {
            return of(this.unitNavigationInfoCache.get(unitId)!);
        }
        return this.http.get<IResponseWrapper<UnitNavigationInfoDto>>(`${environment.apiUrl}/Units/${unitId}/UnitNavigationInfo`).pipe(
            catchError(() => of(null)),
            map((response) => response?.data),
            tap((data) => {
                if (unitId && data) {
                    this.unitNavigationInfoCache.set(unitId, data);
                }
            }),
        );
    }
    getPublicAddressInfoResource(unitId: Signal<number>): ResourceRef<AddressDto[] | null> {
        const url = computed(() => (unitId() ? `${environment.apiUrl}/Units/${unitId()}/PublicAddressInfo` : null));
        return this.http.getResource<AddressDto[]>(url);
    }

    getNeighborhoodInfoResource(unitId: Signal<number>): ResourceRef<INeighborhoodInfoModel | null> {
        const url = computed(() => (unitId() ? `${environment.apiUrl}/Units/${unitId()}/Neighborhood` : null));
        return this.http.getResource<INeighborhoodInfoModel>(url);
    }

    getUnitShareInfoResource(unitId: Signal<number>): ResourceRef<IUnitShareInfo[] | null> {
        const url = computed(() => unitId() ? `${environment.apiUrl}/StockCooperative/${unitId()}/UnitShareInfo` : null);
        return this.http.getResource<IUnitShareInfo[]>(url);
    }

    getOrganizationUnitKeyInfoResource(unitId: Signal<number>): ResourceRef<OrganizationUnitKeyInfo | null> {
        const url = computed(() => unitId() ? `${environment.apiUrl}/StockCooperative/${unitId()}/OrganizationUnitInfo` : null);
        return this.http.getResource<OrganizationUnitKeyInfo>(url);
    }
}
